<template>
  <div class="dish-rating-page">
    <div class="today-header" :style="{ backgroundImage: 'url(' + headerBg + ')' }">
      <h2>今日菜品</h2>
    </div>

    <div class="dish-list">
      <div
        v-for="dish in dishes"
        :key="dish.id"
        class="dish-card"
      >
        <img :src="dish.img_url || defaultDishImg" class="dish-image" />
        <div class="dish-name">{{ dish.name }}</div>
        <div class="rating-row">
          <button
            class="rating-btn btn-good"
            :class="{ active: localGood[dish.id] > 0 }"
            @click="rateGood(dish)"
          >
            <img :src="iconGood" class="icon" />
            <span>赞 {{ dishGood(dish) }}次</span>
          </button>
          <button
            class="rating-btn btn-bad"
            :class="{ active: localBad[dish.id] > 0 }"
            @click="rateBad(dish)"
          >
            <img :src="iconBad" class="icon" />
            <span>踩 {{ dishBad(dish) }}次</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchScreenData, setDishRating } from '../utils/api.js'
import iconGood from '../assets/icon/赞.svg'
import iconBad from '../assets/icon/踩.svg'
import defaultDishImg from '../assets/icon/菜品.svg'
import headerBg from '../assets/header.png'

export default {
  name: 'DishRating',
  data() {
    return {
      iconGood,
      iconBad,
      defaultDishImg,
      headerBg,
      dishes: [],
      // server data counts
      serverGood: {},
      serverBad: {},
      // local accumulated clicks (not yet synced)
      localGood: {},
      localBad: {},
      // pending to sync
      pendingGood: {},
      pendingBad: {},
      flushTimer: null
    }
  },
  computed: {
    dishGood() {
      return dish => (this.serverGood[dish.id] || 0) + (this.localGood[dish.id] || 0)
    },
    dishBad() {
      return dish => (this.serverBad[dish.id] || 0) + (this.localBad[dish.id] || 0)
    }
  },
  mounted() {
    this.loadDishes()
  },
  beforeDestroy() {
    this.flushNow()
    if (this.flushTimer) clearInterval(this.flushTimer)
  },
  methods: {
    async loadDishes() {
      try {
        const data = await fetchScreenData()
        const list = Array.isArray(data) ? data : (data.data || [])
        this.dishes = list
        list.forEach(d => {
          this.$set(this.serverGood, d.id, d.good || 0)
          this.$set(this.serverBad, d.id, d.bad || 0)
          this.$set(this.localGood, d.id, 0)
          this.$set(this.localBad, d.id, 0)
          this.$set(this.pendingGood, d.id, 0)
          this.$set(this.pendingBad, d.id, 0)
        })
      } catch (err) {
        console.error('加载菜品失败:', err)
      }
    },

    rateGood(dish) {
      this.$set(this.localGood, dish.id, (this.localGood[dish.id] || 0) + 1)
      this.$set(this.pendingGood, dish.id, (this.pendingGood[dish.id] || 0) + 1)
      this.scheduleFlush()
    },

    rateBad(dish) {
      this.$set(this.localBad, dish.id, (this.localBad[dish.id] || 0) + 1)
      this.$set(this.pendingBad, dish.id, (this.pendingBad[dish.id] || 0) + 1)
      this.scheduleFlush()
    },

    scheduleFlush() {
      if (this.flushTimer) return
      this.flushTimer = setTimeout(() => {
        this.flushNow()
      }, 2000)
    },

    async flushNow() {
      if (this.flushTimer) {
        clearTimeout(this.flushTimer)
        this.flushTimer = null
      }

      const ids = Object.keys(this.pendingGood).concat(Object.keys(this.pendingBad))
      const uniqueIds = [...new Set(ids)]

      const promises = uniqueIds
        .filter(id => (this.pendingGood[id] || 0) > 0 || (this.pendingBad[id] || 0) > 0)
        .map(id => {
          const g = this.pendingGood[id] || 0
          const b = this.pendingBad[id] || 0
          return setDishRating(id, g, b)
        })

      if (promises.length) {
        try {
          await Promise.all(promises)
          // merge pending into server counts
          uniqueIds.forEach(id => {
            this.serverGood[id] = (this.serverGood[id] || 0) + (this.pendingGood[id] || 0)
            this.serverBad[id] = (this.serverBad[id] || 0) + (this.pendingBad[id] || 0)
          })
          // clear local & pending
          this.dishes.forEach(d => {
            this.localGood[d.id] = 0
            this.localBad[d.id] = 0
            this.pendingGood[d.id] = 0
            this.pendingBad[d.id] = 0
          })
        } catch (err) {
          console.error('提交评分失败:', err)
        }
      }
    }
  }
}
</script>

<style scoped>
.dish-rating-page {
  width: 100%;
  height: 100vh;
  padding: 0 16px 16px 16px;
  box-sizing: border-box;
  background: #0a1628;
  overflow-y: auto;
}

.today-header {
  text-align: center;
  padding: 0 0 16px;
  background: center / cover no-repeat;
  border-radius: 8px;
}

.today-header h2 {
  margin: 0;
  font-size: 28px;
  color: #fff;
  font-weight: 700;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
}

.dish-list {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  padding-bottom: 40px;
}

@media (min-width: 768px) {
  .dish-list {
    grid-template-columns: repeat(6, 1fr);
  }
}

.dish-card {
  background: rgba(26, 54, 93, 0.6);
  border-radius: 12px;
  border: 1px solid rgba(99, 179, 237, 0.2);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 14px;
}

.dish-image {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: contain;
  padding: 16px;
  box-sizing: border-box;
  display: block;
}

.dish-name {
  font-size: 18px;
  font-weight: 600;
  color: #e2e8f0;
  padding: 12px 16px 6px;
  text-align: center;
}

.rating-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 6px 16px 0;
}

@media (min-width: 1800px) {
  .rating-row {
    flex-direction: row;
    gap: 24px;
  }
}

.rating-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid rgba(99, 179, 237, 0.2);
  border-radius: 8px;
  background: rgba(26, 54, 93, 0.4);
  font-size: 15px;
  color: #a0aec0;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
  outline: none;
}

.rating-btn:active {
  transform: scale(0.95);
}

.rating-btn .icon {
  width: 22px;
  height: 22px;
}

.btn-good {
  border-color: rgba(240, 192, 96, 0.5);
  color: #f0c060;
  background: rgba(240, 192, 96, 0.08);
}

.btn-good.active {
  border-color: #e6a817;
  color: #1a1a1a;
  background: #e6a817;
}

.btn-bad {
  border-color: rgba(255, 255, 255, 0.12);
  color: #6b7280;
  background: transparent;
}

.btn-bad.active {
  border-color: rgba(255, 255, 255, 0.2);
  color: #9ca3af;
  background: rgba(255, 255, 255, 0.04);
}

/* scrollbar */
.dish-rating-page::-webkit-scrollbar {
  width: 4px;
}
.dish-rating-page::-webkit-scrollbar-thumb {
  background: rgba(99, 179, 237, 0.3);
  border-radius: 2px;
}
</style>
